import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Car, Gauge, ArrowRight, Filter, Shield, Loader2, CheckCircle, Clock, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { publicAPI } from '../services/api';
import useAPI from '../hooks/useAPI';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const Motorhomes = () => {
  const [filter, setFilter] = useState('all');
  const { data: motorhomes, loading, error } = useAPI(() => publicAPI.getMotorhomes(), []);

  const filteredMotorhomes = (motorhomes || []).filter(m => {
    if (filter === 'all') return true;
    if (filter === '4berth') return m.berth === 4;
    if (filter === '5berth') return m.berth === 5;
    if (filter === '6berth') return m.berth === 6;
    if (filter === 'automatic') return m.type === 'automatic';
    if (filter === 'manual') return m.type === 'manual';
    if (filter === '4belts') return m.specifications?.seatbelts === 4;
    if (filter === '5belts') return m.specifications?.seatbelts === 5;
    if (filter === '6belts') return m.specifications?.seatbelts === 6;
    if (filter === 'available') return m.available !== false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading motorhomes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading motorhomes</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Motorhomes for Hire in Suffolk | 4, 5 & 6 Berth Roller Team Fleet"
        rawTitle
        description="Browse our Suffolk motorhome hire fleet - 4, 5 & 6 berth Roller Team motorhomes, automatic & manual, from £120/night with insurance and unlimited mileage. Drive on a standard car licence. Check live availability."
        keywords="motorhomes for hire Suffolk, 4 berth motorhome hire, 5 berth motorhome hire, 6 berth motorhome hire, automatic motorhome hire, Roller Team hire, Essex motorhome hire"
        url="/motorhomes"
        structuredData={generateBreadcrumbSchema([{ name: 'Motorhomes for Hire', path: '/motorhomes' }])}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Motorhome Fleet</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Choose from our selection of modern, well-maintained motorhomes. All vehicles can be driven on a standard UK driving licence.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="bg-white py-6 sticky top-[72px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-5 w-5 text-slate-600" />
            <span className="text-sm font-semibold text-slate-700 mr-2">Filter by:</span>
            <Button 
              size="sm" 
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
            >
              All Motorhomes
            </Button>
            <Button 
              size="sm" 
              variant={filter === 'available' ? 'default' : 'outline'}
              onClick={() => setFilter('available')}
              className={filter === 'available' ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-600 hover:bg-green-50'}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Available Now
            </Button>
            <Button 
              size="sm" 
              variant={filter === '4berth' ? 'default' : 'outline'}
              onClick={() => setFilter('4berth')}
            >
              4 Berth
            </Button>
            <Button 
              size="sm" 
              variant={filter === '5berth' ? 'default' : 'outline'}
              onClick={() => setFilter('5berth')}
              data-testid="filter-5berth"
            >
              5 Berth
            </Button>
            <Button 
              size="sm" 
              variant={filter === '6berth' ? 'default' : 'outline'}
              onClick={() => setFilter('6berth')}
            >
              6 Berth
            </Button>
            <Button 
              size="sm" 
              variant={filter === 'automatic' ? 'default' : 'outline'}
              onClick={() => setFilter('automatic')}
            >
              Automatic
            </Button>
            <Button 
              size="sm" 
              variant={filter === 'manual' ? 'default' : 'outline'}
              onClick={() => setFilter('manual')}
            >
              Manual
            </Button>
            <Button 
              size="sm" 
              variant={filter === '4belts' ? 'default' : 'outline'}
              onClick={() => setFilter('4belts')}
            >
              4 Belts
            </Button>
            <Button 
              size="sm" 
              variant={filter === '5belts' ? 'default' : 'outline'}
              onClick={() => setFilter('5belts')}
              data-testid="filter-5belts"
            >
              5 Belts
            </Button>
            <Button 
              size="sm" 
              variant={filter === '6belts' ? 'default' : 'outline'}
              onClick={() => setFilter('6belts')}
            >
              6 Belts
            </Button>
          </div>
        </div>
      </section>

      {/* Camping & Caravanning Club Banner */}
      <section className="py-8 bg-red-50 border-y border-red-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/sseihcg6_Screenshot%202026-01-11%20103414.png"
                alt="The Camping and Caravanning Club"
                className="h-16 w-auto"
              />
              <div>
                <p className="font-bold text-slate-900">Official Recommended Supplier</p>
                <p className="text-sm text-slate-600">Get discounted rates at club sites when you hire from us</p>
              </div>
            </div>
            <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Pet-Friendly & Collection Times Banner */}
      <section className="py-4 bg-gradient-to-r from-amber-50 via-green-50 to-blue-50 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            {/* Pet Info */}
            <div className="flex items-center gap-3">
              <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="font-semibold text-amber-700">Pets Welcome!</span>
              <span className="text-slate-600">1 pet <strong className="text-amber-600">£20</strong></span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600">2 pets <strong className="text-amber-600">£40</strong></span>
            </div>
            {/* Divider */}
            <div className="hidden md:block w-px h-6 bg-slate-300"></div>
            {/* Times Info */}
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-green-600" />
              <span className="font-semibold text-slate-800">FREE Times:</span>
              <span className="text-slate-600">Collection:</span>
              <span className="font-bold text-green-600">3:00 PM</span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600">Drop-off:</span>
              <span className="font-bold text-amber-600">11:00 AM</span>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-2">Select pets & extend times via extras during booking</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-blue-50 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Welcome to Millie's Motorhome Hire</h2>
            <p className="text-slate-700 leading-relaxed">
              Established in 2022, we are a family-run business operating in Suffolk. Our small business is named after our daughter, Millie! 
              Below is a description of each of the motorhomes in our fleet and an overview of the facilities each one has. All of our motorhomes 
              can be driven on a standard car (B1) licence, making them accessible to most drivers.
            </p>
          </div>
        </div>
      </section>

      {/* Motorhomes Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {filter === 'all' ? 'All Motorhomes' : 
               filter === '4berth' ? 'Four Berth Motorhomes' :
               filter === '5berth' ? 'Five Berth Motorhomes' :
               filter === '6berth' ? 'Six Berth Motorhomes' :
               filter === 'automatic' ? 'Automatic Motorhomes' : 
               filter === 'manual' ? 'Manual Motorhomes' :
               filter === '4belts' ? '4 Travelling Belts' : 
               filter === '5belts' ? '5 Travelling Belts' : 
               filter === '6belts' ? '6 Travelling Belts' :
               filter === 'available' ? 'Available Now' : 'All Motorhomes'}
            </h2>
            <p className="text-slate-600">Showing {filteredMotorhomes.length} motorhome{filteredMotorhomes.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMotorhomes.map((motorhome) => (
              <Card key={motorhome.id || motorhome.slug} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="relative">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={motorhome.images[0] + '?w=600'}
                      alt={motorhome.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className="bg-white text-slate-900 hover:bg-white">
                      {motorhome.berth} Berth
                    </Badge>
                    <Badge className="bg-blue-600 text-white hover:bg-blue-700 capitalize">
                      {motorhome.type}
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    {motorhome.availableFrom ? (
                      <Badge className="bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1 animate-pulse">
                        <Calendar className="h-3 w-3" />
                        Book Now for {motorhome.availableFrom}!
                      </Badge>
                    ) : motorhome.available !== false ? (
                      <Badge className="bg-green-500 text-white hover:bg-green-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Available
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Check Dates
                      </Badge>
                    )}
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2 text-slate-900">{motorhome.name}</h3>
                  
                  <div className="flex gap-4 mb-4 text-sm text-slate-600 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>Sleeps {motorhome.specifications.sleeps}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Car className="h-4 w-4" />
                      <span className="capitalize">{motorhome.specifications.transmission}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Gauge className="h-4 w-4" />
                      <span>{motorhome.specifications.length}</span>
                    </div>
                    {motorhome.specifications.seatbelts && (
                      <div className="flex items-center gap-1">
                        <Shield className="h-4 w-4" />
                        <span>{motorhome.specifications.seatbelts} Belts</span>
                      </div>
                    )}
                  </div>

                  <p className="text-slate-700 mb-4 line-clamp-3">{motorhome.description}</p>

                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Key Features:</p>
                    <ul className="space-y-1">
                      {motorhome.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t pt-4 mb-4">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">From</p>
                        <p className="text-3xl font-bold text-blue-600">£{motorhome.pricePerNight}</p>
                      </div>
                      <p className="text-slate-600">per night</p>
                    </div>
                  </div>

                  <Button asChild className="w-full" size="lg">
                    <Link to={`/motorhomes/${motorhome.slug}`}>
                      View Details & Book
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredMotorhomes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-xl text-slate-600">No motorhomes found matching your filters.</p>
              <Button onClick={() => setFilter('all')} className="mt-4">
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Can't find what you're looking for?</h2>
          <p className="text-xl text-slate-300 mb-8">
            We're always expanding our fleet. Get in touch and we'll do our best to help!
          </p>
          <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-gray-100">
            <a href="mailto:milliesmotorhomehire@gmail.com">Contact Us</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Motorhomes;
